import React, { useEffect } from 'react';
import { Switch, Route, RouteProps } from 'react-router-dom';
import { LazyArtistMain } from '../Artist';
import { LazyTeamRoute } from '../../features/Team';
import { LazySubscribeRoute } from '../../features/Subscribe';
import { useActivityPage } from '../../features/Activity/lib/useActivityPage';
import { LoadingIndicator } from '../../shared/components/LoadingIndicator';
import { ErrorBoundary, createLogger } from '../../features/errorLogging';
import { useBannerActions } from '../../features/Banner';
import { NotFound } from '../../features/NotFound';

const logger = createLogger('Main');

function MainError() {
  const { showError, hide } = useBannerActions();

  useEffect(() => {
    const id = 'main-error';

    showError('Something went wrong', {
      id,
      dismissOnRouteChange: false,
    });

    return () => {
      hide(id);
    };
  }, [showError, hide]);

  return null;
}

function MainRoute(props: RouteProps) {
  return (
    <ErrorBoundary logger={logger} fallback={<MainError />}>
      <React.Suspense fallback={<LoadingIndicator />}>
        <Route {...props} />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export function MainRouter() {
  const activityPageIsEnabled = useActivityPage();
  return (
    <Switch>
      <MainRoute
        path={['/access', '/get-access']}
        component={RequestAccessRoute}
        key="access"
      />
      <MainRoute
        path={['/invite/:token', '/distributor/invite/:token']}
        component={InviteRoute}
        key="invite"
      />
      <MainRoute
        path="/add-artist"
        component={AddArtistRoute}
        key="add-artist"
      />
      <MainRoute path="/settings" component={SettingsRoute} key="settings" />
      <MainRoute
        path="/content-mismatch"
        component={ContentMismatchRoute}
        key="content-mismatch"
      />
      <MainRoute
        path="/artist/:artistId"
        component={LazyArtistMain}
        key="artist"
      />
      <MainRoute
        path="/subscribe"
        component={LazySubscribeRoute}
        key="subscribe"
      />
      <MainRoute path="/team" component={LazyTeamRoute} key="team" />
      {activityPageIsEnabled && (
        <MainRoute path="/activity" component={LazyActivityRoute} />
      )}
      <MainRoute exact path="/" component={RootRoute} />
      <MainRoute path="/" component={NotFound} />
    </Switch>
  );
}

const LazyActivityRoute = React.lazy(() =>
  import(/* webpackChunkName: "activity" */ '../../features/Activity'),
);

const RequestAccessRoute = React.lazy(() =>
  import(/* webpackChunkName: "access" */ '../../features/RequestAccess/Route'),
);
const RootRoute = React.lazy(() =>
  import(/* webpackChunkName: "root" */ '../RootRoute'),
);
const SettingsRoute = React.lazy(() =>
  import(
    /* webpackChunkName: "settings" */ '../../features/UserSettings/Route'
  ),
);
const InviteRoute = React.lazy(() =>
  import(
    /* webpackChunkName: "get-access" */ '../../features/AccessControls/Invite/Route'
  ),
);
const ContentMismatchRoute = React.lazy(() =>
  import(
    /* webpackChunkName: "content-mismatch" */ '../../features/ArtistProfile/ContentMismatch/Route'
  ),
);
const AddArtistRoute = React.lazy(() =>
  import(
    /* webpackChunkName: "add-artist" */ '../../features/AccessControls/AddArtistRoute'
  ),
);
